import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatCurrency, formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface EnergySummary {
  total_energy_kwh: number;
  total_energy_cost: number;
  total_idle_cost: number;
  total_session_fee: number;
  total_time_fee: number;
  total_cost: number;
  avg_cost_per_kwh: number;
  peak_energy_percent: number;
  total_sessions: number;
  charged_sessions: number;
}

interface TimeSeriesData {
  date: string;
  energy: number;
  cost: number;
  session_count: number;
  charged_session_count: number;
  avg_cost_per_kwh: number;
}

interface TimeWindowData {
  window: string;
  total_energy: number;
  total_cost: number;
  avg_cost_per_kwh: number;
  session_count: number;
}

interface CostBreakdown {
  category: string;
  amount: number;
  percent: number;
}

interface EnergyReportData {
  summary: EnergySummary;
  time_series: TimeSeriesData[];
  energy_by_time_window: TimeWindowData[];
  cost_breakdown: CostBreakdown[];
}

const COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4'];

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const EnergyConsumptionReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // Filter options state
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Period type state (day/month)
  const [periodType, setPeriodType] = useState<'day' | 'month'>('day');

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof TimeSeriesData>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch chargers when vendor or site changes
  useEffect(() => {
    const fetchChargers = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with appropriate filter parameters
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();

        if (siteId) {
          params.append('site_id', siteId);
        } else if (vendorId) {
          params.append('vendor_id', vendorId);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, { headers });
        const data = await response.json();
        setChargers(data.data || []);

        // Reset charger selection if it's no longer in the filtered list
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (error) {
        console.error('Error fetching chargers:', error);
      }
    };

    fetchChargers();
  }, [vendorId, siteId]); // Re-fetch when vendor or site changes

  // Fetch data
  const { data, loading, error, refetch } = useReportData<EnergyReportData>({
    endpoint: '/api/backend/reports/energy-consumption',
    filters: {
      start_date: startDate,
      end_date: endDate,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!processedTimeSeriesData) return;

    exportToCSV(
      processedTimeSeriesData,
      'energy-consumption',
      [
        { key: 'date', label: periodType === 'day' ? 'Date' : 'Month', format: 'date' },
        { key: 'energy', label: 'Energy (kWh)', format: 'number' },
        { key: 'cost', label: 'Cost', format: 'currency' },
        ...(canSeeTotalSessions ? [{ key: 'session_count', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_session_count', label: 'Charged Sessions', format: 'number' },
        { key: 'avg_cost_per_kwh', label: 'Avg Cost/kWh', format: 'currency' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Theoretical (un-rounded) cost vs the actual billed total. The variance
    // is the accumulated per-session currency rounding.
    const theoreticalCost = data.summary.total_energy_kwh * data.summary.avg_cost_per_kwh;
    const roundingVariance = theoreticalCost - data.summary.total_cost;

    // Energy share per charging time window (充電時段). Only windows that
    // actually carried energy (> 0%) are shown, e.g. "Standard: 50.0%",
    // "Peak: 50.0%". Percentage is of total delivered energy.
    const windowEnergyTotal = (data.energy_by_time_window || []).reduce(
      (sum, w) => sum + (w.total_energy || 0),
      0,
    );
    const energyWindowRows = (data.energy_by_time_window || [])
      .filter(w => (w.total_energy || 0) > 0)
      .map(w => ({
        label: w.window,
        value: `${formatNumber(windowEnergyTotal > 0 ? (w.total_energy / windowEnergyTotal) * 100 : 0, 1)}%`,
      }));

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    if (chargerId) {
      const selectedCharger = chargers.find(c => c.id === chargerId);
      if (selectedCharger) {
        filters.charger = { id: chargerId, name: selectedCharger.name };
      }
    }

    await exportToExcel(
      processedTimeSeriesData,
      'energy-consumption',
      {
        columns: [
          { key: 'date', label: periodType === 'day' ? translate('reports.table.date', { fallback: 'Date' }) : translate('reports.table.month', { fallback: 'Month' }), format: 'date' },
          { key: 'energy', label: translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' }), format: 'number' },
          { key: 'cost', label: translate('reports.table.cost', { fallback: 'Cost' }), format: 'currency' },
          ...(canSeeTotalSessions ? [{ key: 'session_count', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_session_count', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'avg_cost_per_kwh', label: translate('reports.table.avgCostPerKwh', { fallback: 'Avg Cost/kWh' }), format: 'currency' },
        ],
        summary: {
          title: translate('reports.energy.title', { fallback: 'Energy Consumption' }),
          data: [
            {
              label: translate('reports.kpi.totalEnergy', { fallback: 'Total Energy' }),
              value: formatNumber(data.summary.total_energy_kwh, 2) + ' kWh'
            },
            {
              label: translate('reports.kpi.avgCostPerKwh', { fallback: 'Avg Cost per kWh' }),
              value: formatCurrency(data.summary.avg_cost_per_kwh)
            },
            {
              // Theoretical (un-rounded) cost = Total Energy x Avg Cost per kWh.
              label: translate('reports.kpi.theoreticalCost', { fallback: 'Theoretical Cost' }),
              value: formatCurrency(theoreticalCost)
            },
            {
              // Difference vs the actual billed total — surfaces per-session
              // currency rounding.
              label: translate('reports.kpi.roundingVariance', { fallback: 'Rounding Variance' }),
              value: formatCurrency(roundingVariance)
            },
            {
              // Renamed from "Total Cost": the sum of actual billed sessions.
              label: translate('reports.kpi.actualBilledCost', { fallback: 'Actual Billed Cost' }),
              value: formatCurrency(data.summary.total_cost)
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
          ],
          note: translate('reports.energy.roundingNote', {
            fallback: 'Note: Actual Billed Cost represents the sum of actual billed sessions. Due to standard currency rounding at the individual session level, multiplying Total Energy by the Avg Cost per kWh may yield a minor fractional variance.',
          }),
          // Energy split across charging time windows (充電時段). Peak Energy %
          // used to be a single KPI; this shows the full breakdown and only
          // includes windows that actually carried energy (> 0%).
          sections: energyWindowRows.length > 0
            ? [{
                title: translate('reports.energy.energyByWindowTitle', { fallback: 'Energy by Charging Window' }),
                rows: energyWindowRows,
              }]
            : undefined,
        },
        // Totals row for the data sheet (mirrors the on-screen table footer).
        totalsRow: {
          date: translate('reports.table.total', { fallback: 'Total' }),
          energy: totals.energy,
          cost: totals.cost,
          ...(canSeeTotalSessions ? { session_count: totals.session_count } : {}),
          charged_session_count: totals.charged_session_count,
          avg_cost_per_kwh: totals.avg_cost_per_kwh,
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Process time series data based on period type
  const processedTimeSeriesData = useMemo(() => {
    if (!data?.time_series) return [];
    if (periodType === 'day') return data.time_series;

    // Aggregate by month
    const monthlyMap = new Map<string, TimeSeriesData>();

    data.time_series.forEach((item) => {
      const month = item.date.substring(0, 7); // YYYY-MM

      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, {
          date: month,
          energy: 0,
          cost: 0,
          session_count: 0,
          charged_session_count: 0,
          avg_cost_per_kwh: 0,
        });
      }

      const entry = monthlyMap.get(month)!;
      entry.energy += item.energy;
      entry.cost += item.cost;
      entry.session_count += item.session_count;
      entry.charged_session_count += item.charged_session_count;
      entry.avg_cost_per_kwh += item.avg_cost_per_kwh * item.energy; // Weighted average
    });

    // Calculate weighted average for avg_cost_per_kwh
    return Array.from(monthlyMap.values()).map(entry => ({
      ...entry,
      avg_cost_per_kwh: entry.energy > 0 ? entry.avg_cost_per_kwh / entry.energy : 0,
    }));
  }, [data?.time_series, periodType]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!processedTimeSeriesData) return [];

    return [...processedTimeSeriesData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [processedTimeSeriesData, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!processedTimeSeriesData || processedTimeSeriesData.length === 0) {
      return {
        energy: 0,
        cost: 0,
        session_count: 0,
        charged_session_count: 0,
        avg_cost_per_kwh: 0,
      };
    }

    const totalEnergy = processedTimeSeriesData.reduce((sum, row) => sum + row.energy, 0);
    const totalCost = processedTimeSeriesData.reduce((sum, row) => sum + row.cost, 0);
    const totalSessions = processedTimeSeriesData.reduce((sum, row) => sum + row.session_count, 0);
    const totalChargedSessions = processedTimeSeriesData.reduce((sum, row) => sum + row.charged_session_count, 0);

    return {
      energy: totalEnergy,
      cost: totalCost,
      session_count: totalSessions,
      charged_session_count: totalChargedSessions,
      avg_cost_per_kwh: totalEnergy > 0 ? totalCost / totalEnergy : 0,
    };
  }, [processedTimeSeriesData]);

  const handleSort = (property: keyof TimeSeriesData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const timeWindowChartData = useMemo(() => {
    if (!data?.energy_by_time_window) return [];
    return isMobile ? data.energy_by_time_window.slice(0, 6) : data.energy_by_time_window;
  }, [data?.energy_by_time_window, isMobile]);

  return (
    <ReportLayout
      title={translate('reports.energy.title', { fallback: 'Energy Consumption' })}
      description={translate('reports.energy.subtitle', { fallback: 'Energy usage and cost analysis' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          chargerId={chargerId}
          onChargerIdChange={setChargerId}
          sites={sites}
          vendors={vendors}
          chargers={chargers}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={true}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                px: { xs: 1.5, sm: 2 },
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minWidth: 0,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>

              {/* Period Toggle */}
              {tabValue === 0 && (
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={(_, newValue) => newValue && setPeriodType(newValue)}
                  size="small"
                  sx={{
                    mb: 1,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'unset' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 0.5 },
                    },
                  }}
                >
                  <ToggleButton value="day">{translate('reports.groupBy.day', { fallback: 'Day' })}</ToggleButton>
                  <ToggleButton value="month">{translate('reports.groupBy.month', { fallback: 'Month' })}</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {/* Data Table Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.dailyBreakdown', { fallback: 'Daily Breakdown' })} ({sortedData.length} {translate('reports.days', { fallback: 'days' })})
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Paper variant="outlined" sx={mobileRowCardSx}>
                      <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                        {translate('reports.table.total', { fallback: 'Total' })}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.energy, 2)} kWh</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.cost', { fallback: 'Cost' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatCurrency(totals.cost)}</Typography>
                        </Box>
                        {canSeeTotalSessions && (
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(totals.session_count, 0)}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charged_session_count, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.avgCostPerKwh', { fallback: 'Avg Cost/kWh' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatCurrency(totals.avg_cost_per_kwh)}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {sortedData.map((row) => (
                      <Paper key={row.date} variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.75 }}>
                          {periodType === 'day'
                            ? translate('reports.table.date', { fallback: 'Date' })
                            : translate('reports.table.month', { fallback: 'Month' })
                          }
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 1 }}>
                          {periodType === 'day' ? formatDate(row.date, 'long') : row.date}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.energy, 2)} kWh</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.cost', { fallback: 'Cost' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(row.cost)}</Typography>
                          </Box>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.session_count, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.charged_session_count, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.avgCostPerKwh', { fallback: 'Avg Cost/kWh' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(row.avg_cost_per_kwh)}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'date'}
                                direction={orderBy === 'date' ? order : 'asc'}
                                onClick={() => handleSort('date')}
                              >
                                {periodType === 'day'
                                  ? translate('reports.table.date', { fallback: 'Date' })
                                  : translate('reports.table.month', { fallback: 'Month' })
                                }
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'energy'}
                                direction={orderBy === 'energy' ? order : 'asc'}
                                onClick={() => handleSort('energy')}
                              >
                                {translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'cost'}
                                direction={orderBy === 'cost' ? order : 'asc'}
                                onClick={() => handleSort('cost')}
                              >
                                {translate('reports.table.cost', { fallback: 'Cost' })}
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'session_count'}
                                  direction={orderBy === 'session_count' ? order : 'asc'}
                                  onClick={() => handleSort('session_count')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charged_session_count'}
                                  direction={orderBy === 'charged_session_count' ? order : 'asc'}
                                  onClick={() => handleSort('charged_session_count')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'avg_cost_per_kwh'}
                                direction={orderBy === 'avg_cost_per_kwh' ? order : 'asc'}
                                onClick={() => handleSort('avg_cost_per_kwh')}
                              >
                                {translate('reports.table.avgCostPerKwh', { fallback: 'Avg Cost/kWh' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{periodType === 'day' ? formatDate(row.date, 'long') : row.date}</TableCell>
                              <TableCell align="right">{formatNumber(row.energy, 2)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.cost)}</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(row.session_count, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(row.charged_session_count, 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.avg_cost_per_kwh)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow
                            sx={{
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                fontWeight: 600,
                                zIndex: 2,
                              }
                            }}
                          >
                            <TableCell>{translate('reports.table.total', { fallback: 'Total' })}</TableCell>
                            <TableCell align="right">{formatNumber(totals.energy, 2)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.cost)}</TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">{formatNumber(totals.session_count, 0)}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(totals.charged_session_count, 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.avg_cost_per_kwh)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                {/* Energy Delivered Over Time Chart */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3, mt: 3, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.charts.energyOverTime', { fallback: 'Energy Delivered Over Time' })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <AreaChart data={processedTimeSeriesData}>
                      <defs>
                        <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#009A75" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#009A75" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => periodType === 'month' ? date : formatDate(date)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        interval={isMobile ? 'preserveStartEnd' : 0}
                        minTickGap={isMobile ? 24 : 12}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        tickFormatter={(value) => formatNumber(value, 0)}
                        width={isMobile ? 42 : 54}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'energy') return `${formatNumber(value, 2)} kWh`;
                          if (name === 'cost') return formatCurrency(value);
                          return formatNumber(value, 0);
                        }}
                        labelFormatter={(date) => periodType === 'month' ? date : formatDate(date, 'long')}
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E0E0E0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                      <Area
                        type="monotone"
                        dataKey="energy"
                        stroke="#009A75"
                        strokeWidth={isMobile ? 2 : 3}
                        fillOpacity={1}
                        fill="url(#colorEnergy)"
                        name="Energy (kWh)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, minWidth: 0 }}>
                  {/* Energy by Time Window Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.energyByTimeWindow', { fallback: 'Energy by Time Window' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 260 : 300}>
                      <BarChart data={timeWindowChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis
                          dataKey="window"
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          tickFormatter={(value) => formatNumber(value, 0)}
                          width={isMobile ? 42 : 54}
                        />
                        <Tooltip
                          formatter={(value: number) => `${formatNumber(value, 2)} kWh`}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="total_energy" fill="#009A75" name="Energy (kWh)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Cost Breakdown Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.costBreakdown', { fallback: 'Cost Breakdown' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                      <PieChart>
                        <Pie
                          data={data.cost_breakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={isMobile ? false : ({ category, percent }) => `${category}: ${percent.toFixed(1)}%`}
                          outerRadius={isMobile ? 70 : 80}
                          fill="#8884d8"
                          dataKey="amount"
                        >
                          {data.cost_breakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Legend
                          verticalAlign={isMobile ? 'bottom' : 'top'}
                          align="center"
                          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
